'use client';

import { Button } from '@eliadmin/core/src/components/ui/button';
import { Checkbox } from '@eliadmin/core/src/components/ui/checkbox';
import { Input } from '@eliadmin/core/src/components/ui/input';
import { Label } from '@eliadmin/core/src/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@eliadmin/core/src/components/ui/select';
import { Spinner } from '@eliadmin/core/src/components/ui/spinner';
import { useTranslations } from '@eliadmin/core/src/hooks/use-translations';
import { cn } from '@eliadmin/core/src/utils/style-utils';
import { useQuery } from '@tanstack/react-query';
import { useEffect, useId, useState } from 'react';
import { FiSearch, FiX } from 'react-icons/fi';
import {
  buildRealtyFilterWhere,
  buildRealtySearchParams,
  fetchRealties,
  fetchRealtyCities,
  fetchRealtyOfferTypes,
  fetchRealtyTypes,
} from '../../api/realty/realty-api';
import { useLocaleStore } from '@eliadmin/core/src/stores/locale-store';
import type { RealtyCity, RealtyOfferType, RealtyType } from '../../types/realty';
import Link from '@eliadmin/core/src/components/link';
import { ROUTES } from '@eliadmin/core/src/constants/routes';
import { CacheKey } from '@eliadmin/core/src/api/constants';

const priceRangeOptions = [
  {
    name: '0 - 50 000€',
    value: '0-50000',
  },
  {
    name: '50 000€ - 100 000€',
    value: '50000-100000',
  },
  {
    name: '100 000€ - 150 000€',
    value: '100000-150000',
  },
  {
    name: '150 000€',
    value: '150000-X',
  },
];

const homepageOfferTypeOrder = ['Predaj', 'Prenájom', 'Kúpa'];

function orderHomepageOfferTypes(types: RealtyOfferType[]): RealtyOfferType[] {
  return [...types].sort((a, b) => {
    const aIndex = homepageOfferTypeOrder.indexOf(a.name);
    const bIndex = homepageOfferTypeOrder.indexOf(b.name);

    if (aIndex === -1 && bIndex === -1) return a.id - b.id;
    if (aIndex === -1) return 1;
    if (bIndex === -1) return -1;
    return aIndex - bIndex;
  });
}

function getPriceValue(priceRange: string | undefined, index: number): number | undefined {
  if (priceRange) {
    const parsedValue = parseInt(priceRange.split('-')[index]);
    return !isNaN(parsedValue) ? parsedValue : undefined;
  }
  return undefined;
}

export const HomepageRealtySearch = () => {
  const { t } = useTranslations();
  const locale = useLocaleStore(s => s.currentLocale);
  const searchId = useId();

  const { data: locationOptions } = useQuery<{ data: RealtyCity[] }>({
    queryKey: [CacheKey.REALTY_CITIES, locale],
    queryFn: () => fetchRealtyCities({ locale }),
  });

  const { data: realtyTypeOptions } = useQuery<{ data: RealtyType[] }>({
    queryKey: [CacheKey.REALTY_TYPES, locale],
    queryFn: () => fetchRealtyTypes({ locale }),
  });

  const { data: realtyOfferTypeOptions } = useQuery<{ data: RealtyOfferType[] }>({
    queryKey: [CacheKey.REALTY_OFFER_TYPES, locale],
    queryFn: () => fetchRealtyOfferTypes({ locale }),
  });

  const [location, setLocation] = useState<string>();
  const [realtyType, setRealtyType] = useState<string[]>([]);
  const [keyword, setKeyword] = useState<string>('');
  const [priceRange, setPriceRange] = useState<string>();

  const [selectedTransactionTypes, setSelectedTransactionTypes] = useState<number[]>([]);

  const [debouncingFetch, setDebouncingFetch] = useState(false);
  const [numberOfResults, setNumberOfResults] = useState(0);

  useEffect(() => {
    const abortController = new AbortController();
    setDebouncingFetch(true);
    const timeoutId = setTimeout(() => {
      fetchRealties({
        locale,
        signal: abortController.signal,
        query: {
          where: buildRealtyFilterWhere({
            type_id: realtyType ? realtyType : undefined,
            offer_type_id: selectedTransactionTypes,
            city_id: location,
          }),
          price_from: getPriceValue(priceRange, 0),
          price_to: getPriceValue(priceRange, 1),
          search: keyword.length > 1 ? keyword : undefined,
        },
      })
        .then(res => {
          if (!abortController.signal.aborted) setNumberOfResults(res.meta.total);
        })
        .catch(() => {
          if (!abortController.signal.aborted) setNumberOfResults(0);
        })
        .finally(() => {
          if (!abortController.signal.aborted) setDebouncingFetch(false);
        });
    }, 1000);

    return () => {
      clearTimeout(timeoutId);
      abortController.abort();
    };
  }, [keyword, locale, location, priceRange, realtyType, selectedTransactionTypes]);

  return (
    <section
      className={cn(
        'custom-section',
        'custom-container',
        'relative z-10 mx-auto -translate-y-20 md:mb-8',
      )}
      data-eli-component="homepage-realty-search"
    >
      {realtyOfferTypeOptions?.data && (
        <div
          className="absolute -top-[40px] left-6 right-6 flex h-[40px] overflow-hidden rounded-t-md bg-white md:left-[40px] md:right-auto md:w-auto"
          data-eli-part="homepage-offer-type-tabs"
        >
          {orderHomepageOfferTypes(realtyOfferTypeOptions.data).map(type => {
            const checked = selectedTransactionTypes.includes(type.id);

            return (
              <label
                key={type.id}
                htmlFor={`${searchId}-offer-${type.id}`}
                className={cn(
                  'flex h-full min-w-0 flex-1 basis-0 cursor-pointer items-center justify-center gap-1 bg-input/60 px-1 md:flex-none md:basis-auto md:gap-3 md:px-4',
                  checked && 'bg-primary text-primary-foreground',
                )}
                data-eli-part="homepage-offer-type-option"
                data-offer-type-id={type.id}
              >
                <Checkbox
                  id={`${searchId}-offer-${type.id}`}
                  aria-label={type.name}
                  checked={checked}
                  onCheckedChange={checked => {
                    setSelectedTransactionTypes(prev =>
                      checked === true
                        ? prev.includes(type.id)
                          ? prev
                          : [...prev, type.id]
                        : prev.filter(v => v !== type.id),
                    );
                  }}
                  className={cn(
                    'h-6 w-6 rounded-[4px]',
                    checked && 'border-none !bg-[#2E5A21] outline-none ring-0',
                  )}
                />
                <span className="whitespace-nowrap">{type.name}</span>
              </label>
            );
          })}
        </div>
      )}
      <div
        className="grid w-full grid-cols-1 items-end justify-center gap-4 rounded-[16px] bg-white p-6 md:grid-cols-2 lg:grid-cols-5"
        data-eli-part="homepage-realty-search-form"
      >
        <HomepageRealtySearchSelect
          label={t('text1407')}
          placeholder={t('text1408')}
          options={locationOptions?.data ?? []}
          value={location}
          onValueChange={setLocation}
          isClearable
        />
        <HomepageRealtySearchSelect
          label={t('text1409')}
          placeholder={t('text1410')}
          options={realtyTypeOptions?.data ?? []}
          value={realtyType?.[0]}
          onValueChange={value => (value ? setRealtyType([value]) : setRealtyType([]))}
          isClearable
        />
        <div className="">
          <Label htmlFor={`${searchId}-keyword`}>{t('text747')}</Label>
          <Input
            id={`${searchId}-keyword`}
            value={keyword}
            placeholder={t('text1411')}
            onChange={e => setKeyword(e.target.value)}
            classNames={{
              wrapper: 'sm:h-[64px]',
              main: 'bg-[#F3F3F3] placeholder:text-gray-400',
            }}
          />
        </div>
        <HomepageRealtySearchSelect
          label={t('text164')}
          placeholder={t('text1412')}
          options={priceRangeOptions}
          value={priceRange}
          onValueChange={setPriceRange}
          isClearable
        />
        <Button
          className="min-h-[54px] gap-1 whitespace-nowrap text-base sm:h-full md:col-span-2 lg:col-span-1"
          asChild
        >
          <Link
            aria-label={debouncingFetch ? t('text3430') : undefined}
            href={`${ROUTES.REALTIES()}?${buildRealtySearchParams({
              type: realtyType?.[0],
              offer_type: selectedTransactionTypes,
              price_from: getPriceValue(priceRange, 0),
              price_to: getPriceValue(priceRange, 1),
              search: keyword,
              city: location,
            })}`}
            prefetch={false}
          >
            {debouncingFetch ? (
              <Spinner className={'h-5 w-5 text-primary-foreground md:h-6 md:w-6 lg:h-7 lg:w-7'} />
            ) : (
              <>
                {numberOfResults > 0 && (
                  <>
                    <FiSearch className={'min-h-5 min-w-5'} />
                    <span>
                      {numberOfResults}{' '}
                      {numberOfResults === 1
                        ? t('text1423')
                        : numberOfResults < 5
                          ? t('text1424')
                          : t('text1413')}
                    </span>
                  </>
                )}
                {numberOfResults === 0 && (
                  <>
                    <span>{t('text1419')}</span>
                  </>
                )}
              </>
            )}
          </Link>
        </Button>
      </div>
    </section>
  );
};

type HomepageRealtySearchSelectProps = {
  label: string;
  placeholder?: string;
  options: { name: string; id?: number; value?: string }[];
  value?: string;
  onValueChange: (value?: string) => void;
  className?: {
    wrapper: string;
  };
  isClearable?: boolean;
};

const HomepageRealtySearchSelect = ({
  label,
  placeholder,
  options,
  value,
  onValueChange,
  className,
  isClearable = false,
}: HomepageRealtySearchSelectProps) => {
  const selectId = useId();
  return (
    <div
      className={cn(
        'col-span-1 flex w-full flex-col gap-2 sm:w-auto',
        'ea-cart__shipment-country-select-wrapper',
        className?.wrapper,
      )}
    >
      <Label htmlFor={selectId}>{label}</Label>
      <Select value={value} onValueChange={onValueChange}>
        <SelectTrigger
          id={selectId}
          aria-label={label}
          className={cn(
            'gap-0 bg-[#F3F3F3] pl-0 sm:h-[64px]',
            'ea-cart__shipment-country-select-trigger',
          )}
          isClearable={!!(isClearable && value)}
          innerTriggerClassName="data-[placeholder]:text-gray-400"
          onClear={() => onValueChange('')}
          clearIcon={
            <FiX
              className={cn(
                'h-4 w-4 cursor-pointer opacity-50 hover:opacity-100',
                'ea-select__clear-icon',
              )}
            />
          }
          clearableIconPossition="right"
        >
          <SelectValue
            placeholder={placeholder}
            className={cn('', 'ea-cart__shipment-country-select-value')}
          />
        </SelectTrigger>
        <SelectContent>
          {options.map((option, key) => (
            <SelectItem
              key={key}
              value={option.value ?? option.id?.toString() ?? 'error'}
              className={cn('ea-realty-filter__location-item')}
            >
              {option.name}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  );
};
